import { useCallback, useMemo, useState } from 'react'
import { gql } from '@apollo/client'

import { getColorFromResponse } from 'utils/colors'
import { MerchantAvatar } from 'components/MerchantAvatar/MerchantAvatar'

import { UseIconAvatarIconFragment } from '../../../types/gql-types/UseIconAvatarIconFragment'
import { getImageUrl } from '../IconAvatar.utils'

interface UseIconAvatarProps {
  icon: UseIconAvatarIconFragment | null
  defaultLetter: string
  defaultBgColor?: string
  defaultUrl?: string
}

export const useIconAvatar = ({
  icon,
  defaultLetter,
  defaultBgColor,
  defaultUrl,
}: UseIconAvatarProps) => {
  const avatarUrl = getImageUrl(icon)
  const [isAvatarError, setIsAvatarError] = useState(false)

  const handleUserDefinedContentError = useCallback(() => {
    setIsAvatarError(true)
  }, [])

  const avatarProps = useMemo(
    () => ({
      userDefinedContent:
        (avatarUrl ?? defaultUrl) && !isAvatarError ? (
          <MerchantAvatar
            merchantAvatarUrl={avatarUrl ?? defaultUrl}
            onError={handleUserDefinedContentError}
          />
        ) : undefined,
      text: icon?.letter || defaultLetter,
      bgColor: getColorFromResponse(icon?.colour ?? defaultBgColor),
    }),
    [
      avatarUrl,
      defaultUrl,
      isAvatarError,
      handleUserDefinedContentError,
      icon?.letter,
      icon?.colour,
      defaultLetter,
      defaultBgColor,
    ]
  )

  return avatarProps
}

useIconAvatar.fragments = {
  Icon: gql`
    fragment UseIconAvatarIconFragment on Icon {
      colour
      letter
      ...GetImageUrlIconFragment
    }

    ${getImageUrl.fragments.Icon}
  `,
}
